import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, inject, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Router, RoutesRecognized, type Routes } from '@angular/router';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { filter, of, switchMap, type Observable } from 'rxjs';
import { routes } from './app.routes';
import { PagePath } from './enums/page-path.enum';
import { authInterceptor } from './interceptors/auth.interceptor';
import { AuthService } from './services/auth.service';
import { ConfigResourceService } from './services/config-resource.service';
import { NavigationContext } from './services/navigation.context.service';
import { RouteOptionsService } from './services/route-options.service';
import { RoutesUtilityService } from './services/routes-utility.service';
import { ALL_STORE_STATES } from './store/store-states';

function initializeNavigationContext(): void {
  const router = inject(Router);
  const navigationContext = inject(NavigationContext);

  // Need to store our url params on route change since ActivatedRoute only contains the route information about
  // components loaded in an router outlet. Since we need to access url params from other parts of the application
  // we need to watch for route changes and store the relevant information. Read more at:
  // https://stackoverflow.com/questions/42947133/parent-components-gets-empty-params-from-activatedroute
  // Setting up NavigationContext population before any navigation happens
  // This ensures url params are available for resolvers and guards
  router.events.pipe(filter(event => event instanceof RoutesRecognized)).subscribe({
    next: (event: RoutesRecognized) => {
      navigationContext.setUrlParams(event?.state?.root?.firstChild?.params ?? null);
      navigationContext.setQueryParams(event?.state?.root?.firstChild?.queryParams ?? null);
    }
  });
}

// The initializer function
function initializeAppRoutes$(): Observable<boolean> {
  const configService = inject(ConfigResourceService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const routeOptionsService = inject(RouteOptionsService);

  return configService.loadConfig$().pipe(
    switchMap(config => {
      if (config) {
        const defaultRoutes: Routes = router.config;
        const disabledRoutes: PagePath[] = routeOptionsService.getDisabledRoutes(configService);
        const configRoutes: Routes = config.pages.reduce<Routes>((enabledRoutes, page) => {
          if (!page.disabled) {
            const pagePath: PagePath | null = RoutesUtilityService.convertPageNameToPagePath(page.pageName);

            if (pagePath && !disabledRoutes.includes(pagePath)) {
              const normalizedPagePath: string | undefined = RoutesUtilityService.normalizePagePath(pagePath);
              const defaultRoute = defaultRoutes.find(route => route.path?.toLowerCase() === normalizedPagePath?.toLowerCase());

              if (
                defaultRoute &&
                !enabledRoutes.some(enabledRoute => enabledRoute.path?.toLowerCase() === defaultRoute.path?.toLowerCase())
              ) {
                enabledRoutes.push({
                  ...defaultRoute,
                  ...(page.redirectTo && { redirectTo: page.redirectTo }),
                  data: { title: page.title.replaceAll('{{cfg.ClientName}}', '[[ClientName]]') }
                });
              }
            }
          }

          return enabledRoutes;
        }, []);

        // redirect to homepage when we hit root path
        if (config.homepage) {
          const homePagePath: PagePath | null = RoutesUtilityService.convertPageNameToPagePath(config.homepage);
          const redirectTo: string | undefined = RoutesUtilityService.normalizePagePath(homePagePath ?? PagePath.HOME);

          configRoutes.unshift({
            path: '',
            redirectTo,
            pathMatch: 'full'
          });
          configRoutes.push({
            path: '**',
            redirectTo
          });
        } else {
          configRoutes.push({
            path: '**',
            redirectTo: RoutesUtilityService.normalizePagePath(PagePath.HOME)
          });
        }

        router.resetConfig(configRoutes);

        if (!authService.isCurrentTokenValid()) {
          return authService.loadJWT$();
        }
      }
      return of(false);
    })
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideStore(
      ALL_STORE_STATES,
      withNgxsStoragePlugin({
        keys: ['app', 'plans', 'drugs', 'pharmacies', 'demographics', 'runtime', 'googleDataLayerTag', 'deepLinkingParams'].sort(),
        storage: 1 // SessionStorage
      }),
      withNgxsLoggerPlugin({
        disabled: false
      })
    ),
    // Register the initializer
    provideAppInitializer(() => {
      initializeNavigationContext();
      return initializeAppRoutes$();
    }),
    {
      provide: APP_BASE_HREF,
      useFactory: (platformLocation: PlatformLocation) => platformLocation.getBaseHrefFromDOM(),
      deps: [PlatformLocation]
    },
    { provide: LOCALE_ID, useValue: 'en-US' },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { disableClose: false, hasBackdrop: true, minWidth: '500px', minHeight: '200px' } }
  ]
};
